import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, GripVertical, Pencil, Trash2, Rocket, CalendarDays, FileText, Loader2, CheckCircle2, ListChecks, HelpCircle, BookOpen, Plus, X, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function DraftModule({ mod, index, onRename, onRemove, navigate }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mod.id });
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(mod.title);
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1 };
  const commit = () => { setEditing(false); if (title.trim() && title !== mod.title) onRename(mod.id, title.trim()); };
  return (
    <div ref={setNodeRef} style={style} className={`flex items-start gap-3 p-3 rounded-xl border bg-white ${isDragging ? 'shadow-lg border-[#1B2A4A]' : 'border-stone-200'}`} data-testid={`draft-module-${mod.id}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-600 mt-1" data-testid={`draft-drag-${mod.id}`}><GripVertical className="w-4 h-4" /></button>
      <div className="w-7 h-7 rounded-full bg-[#1B2A4A] text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{index + 1}</div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <Input value={title} onChange={e => setTitle(e.target.value)} onBlur={commit} onKeyDown={e => e.key === 'Enter' && commit()} autoFocus className="h-8 rounded-lg text-sm" data-testid={`draft-title-input-${mod.id}`} />
        ) : (
          <button onClick={() => setEditing(true)} className="text-left group" data-testid={`draft-title-${mod.id}`}>
            <p className="text-sm font-semibold text-stone-900 group-hover:text-[#D9480F] transition-colors">{mod.title} <Pencil className="w-3 h-3 inline ml-1 opacity-0 group-hover:opacity-100" /></p>
          </button>
        )}
        <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">{mod.description}</p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[11px] text-stone-400">
          {mod.day_label && <Badge className="bg-[#D9480F]/10 text-[#D9480F] text-[10px]">{mod.day_label}</Badge>}
          <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" />{mod.lesson_count} lessons</span>
          <span className="flex items-center gap-1"><HelpCircle className="w-3 h-3" />{mod.question_count} questions</span>
          <span className="flex items-center gap-1"><ListChecks className="w-3 h-3" />{mod.checklist_count} practical</span>
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/module/${mod.id}`)} className="h-8 w-8 p-0 text-stone-500 hover:text-[#1B2A4A]" title="Edit content" data-testid={`draft-edit-${mod.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => onRemove(mod.id)} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600" title="Remove" data-testid={`draft-remove-${mod.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function ScheduleDay({ day, index, modulesById, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...day, [k]: v });
  return (
    <Card className="rounded-xl border border-stone-200 bg-white" data-testid={`schedule-day-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Input value={day.label} onChange={e => set('label', e.target.value)} className="h-8 w-28 rounded-lg text-sm font-semibold" data-testid={`schedule-label-${index}`} />
          <Input value={day.title} onChange={e => set('title', e.target.value)} placeholder="Theme of the day" className="h-8 flex-1 rounded-lg text-sm" data-testid={`schedule-title-${index}`} />
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0 text-stone-400 hover:text-red-600" data-testid={`schedule-remove-${index}`}><X className="w-4 h-4" /></Button>
        </div>
        <Textarea value={day.focus} onChange={e => set('focus', e.target.value)} placeholder="Goal for the day" rows={2} className="rounded-lg text-sm mb-2" data-testid={`schedule-focus-${index}`} />
        <div className="flex flex-wrap gap-1.5 mb-2">
          {day.module_ids.map(mid => modulesById[mid] && <Badge key={mid} className="bg-[#1B2A4A]/10 text-[#1B2A4A] text-[10px] font-normal">{modulesById[mid].title}</Badge>)}
          {day.module_ids.length === 0 && <span className="text-[11px] text-stone-400">No modules this day (reorder above to assign)</span>}
        </div>
        <Label className="text-[11px] text-stone-500">Activities (one per line)</Label>
        <Textarea value={day.activities.join('\n')} onChange={e => set('activities', e.target.value.split('\n'))} rows={3} className="rounded-lg text-sm mt-1" data-testid={`schedule-activities-${index}`} />
      </CardContent>
    </Card>
  );
}

export default function PathwayReviewPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [doc, setDoc] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [paths, setPaths] = useState([]);
  const [pathId, setPathId] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [regen, setRegen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([api('get', `/documents/${docId}/pathway`), api('get', '/learning-paths')]);
      setDoc(d.data); setSchedule(d.data.schedule || []); setPaths(p.data);
      if (d.data.path_id) setPathId(d.data.path_id);
    } catch { toast.error('Failed to load pathway'); navigate('/admin'); }
  }, [api, docId, navigate]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (doc?.status !== 'processing') return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [doc?.status, load]);

  const persist = async (payload) => {
    setSaving(true);
    try { const { data } = await api('put', `/documents/${docId}/pathway`, payload); setDoc(data); setSchedule(data.schedule || []); }
    catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = doc.modules.map(m => m.id);
    persist({ module_ids: arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)) });
  };
  const rename = (id, title) => persist({ module_titles: { [id]: title } });
  const remove = async (id) => {
    if (!window.confirm('Remove this module from the pathway? Its content will be deleted.')) return;
    try { await api('delete', `/training-modules/${id}`); load(); } catch { toast.error('Failed to remove'); }
  };
  const saveSchedule = () => persist({ schedule });
  const regenSchedule = async () => {
    setRegen(true);
    try { const { data } = await api('post', `/documents/${docId}/schedule/regenerate`); setDoc(data); setSchedule(data.schedule || []); toast.success('Schedule regenerated'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to regenerate'); } finally { setRegen(false); }
  };
  const publish = async () => {
    setPublishing(true);
    try {
      await persist({ schedule });
      const { data } = await api('post', `/documents/${docId}/publish`, { path_id: pathId === 'standalone' ? '' : pathId });
      toast.success(data.message);
      navigate('/admin');
    } catch (err) { toast.error(err.response?.data?.detail || 'Publish failed'); }
    finally { setPublishing(false); }
  };

  if (!doc) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-[#1B2A4A]" /></div>;
  const modulesById = Object.fromEntries((doc.modules || []).map(m => [m.id, m]));
  const isReview = doc.status === 'review';
  const totalQuestions = doc.modules.reduce((s, m) => s + m.question_count, 0);

  return (
    <div className="pb-24 md:pb-10" data-testid="pathway-review-page">
      <Button variant="ghost" onClick={() => navigate('/admin')} className="text-stone-600 hover:text-stone-900 mb-3 -ml-2 rounded-xl" data-testid="back-to-admin"><ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard</Button>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Review Pathway</h1>
          <Badge className={isReview ? 'bg-amber-100 text-amber-700' : doc.status === 'processing' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'} data-testid="pathway-status">{isReview ? 'Draft — not visible to staff' : doc.status}</Badge>
        </div>
        <p className="text-sm text-stone-500 mt-1 flex items-center gap-2 flex-wrap"><FileText className="w-4 h-4" /> {doc.original_filename} · {doc.page_count} pages · {doc.modules.length} modules · {totalQuestions} questions{doc.outline?.structure === 'day_based' && <Badge className="bg-[#D9480F]/10 text-[#D9480F] text-[10px]">Follows document's day structure</Badge>}</p>
        {doc.outline?.summary && <p className="text-sm text-stone-600 mt-2 max-w-3xl">{doc.outline.summary}</p>}
        {doc.outline?.excluded?.length > 0 && (
          <details className="mt-2 text-xs text-stone-500" data-testid="excluded-sections">
            <summary className="cursor-pointer hover:text-stone-700">{doc.outline.excluded.length} document part{doc.outline.excluded.length > 1 ? 's' : ''} skipped (forms, references, TOC)</summary>
            <ul className="mt-1 ml-4 list-disc space-y-0.5">{doc.outline.excluded.map((e, i) => <li key={i}>{e.title} <span className="text-stone-400">— {e.reason}</span></li>)}</ul>
          </details>
        )}
      </motion.div>

      {doc.status === 'failed' && (
        <Card className="rounded-2xl border border-red-200 bg-red-50 mb-6" data-testid="pathway-failed"><CardContent className="p-5 text-sm text-red-900">{doc.error || 'Processing failed. Please upload the document again.'}</CardContent></Card>
      )}

      {doc.status === 'processing' && (
        <Card className="rounded-2xl border border-blue-200 bg-blue-50 mb-6"><CardContent className="p-5 flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /><p className="text-sm text-blue-900">{doc.progress || 'AI is analyzing the document…'}</p></CardContent></Card>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Modules <span className="text-stone-400 font-normal text-sm">(drag to reorder, click title to rename)</span></h2>
            {saving && <span className="text-xs text-stone-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving</span>}
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={doc.modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" data-testid="draft-modules-list">
                {doc.modules.map((m, i) => <DraftModule key={m.id} mod={m} index={i} onRename={rename} onRemove={remove} navigate={navigate} />)}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {isReview && (
            <Card className="rounded-2xl border-0 bg-[#1B2A4A] text-white shadow-md" data-testid="publish-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3"><Rocket className="w-5 h-5 text-[#D9480F]" /><h3 className="font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Publish to staff</h3></div>
                <Label className="text-white/70 text-xs">Learning Path</Label>
                <Select value={pathId || 'standalone'} onValueChange={setPathId}>
                  <SelectTrigger className="rounded-xl mt-1 bg-white text-stone-900" data-testid="publish-path-select"><SelectValue placeholder="Choose a path" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standalone">Standalone (no path)</SelectItem>
                    {paths.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.position ? ` (${p.position})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-white/60 mt-2">Modules are appended to the path in the order above. Each requires manager sign-off before the next unlocks.</p>
                <Button onClick={publish} disabled={publishing || doc.modules.length === 0} className="w-full mt-4 bg-[#D9480F] hover:bg-[#c2410c] text-white rounded-xl" data-testid="publish-btn">
                  {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Publish {doc.modules.length} modules</>}
                </Button>
              </CardContent>
            </Card>
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}><CalendarDays className="w-5 h-5 text-[#D9480F]" /> Training Schedule</h2>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={regenSchedule} disabled={regen} className="rounded-xl border-stone-200 text-xs" data-testid="regenerate-schedule-btn">{regen ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Sparkles className="w-3 h-3 mr-1" /> AI</>}</Button>
                <Button variant="outline" size="sm" onClick={() => setSchedule([...schedule, { id: `new-${Date.now()}`, day: schedule.length + 1, label: `Day ${schedule.length + 1}`, title: '', focus: '', module_ids: [], activities: [] }])} className="rounded-xl border-stone-200 text-xs" data-testid="add-schedule-day-btn"><Plus className="w-3 h-3 mr-1" /> Day</Button>
                <Button size="sm" onClick={saveSchedule} disabled={saving} className="rounded-xl bg-[#1B2A4A] text-white text-xs hover:bg-[#243658]" data-testid="save-schedule-btn">Save schedule</Button>
              </div>
            </div>
            {schedule.length === 0 ? (
              <Card className="rounded-xl border border-dashed border-stone-300 bg-stone-50"><CardContent className="p-5 text-center text-sm text-stone-500">{doc.page_count > 15 ? 'No schedule generated yet.' : 'Schedules are auto-built for documents over 15 pages. Add days manually if needed.'}</CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="schedule-list">
                {schedule.map((d, i) => <ScheduleDay key={d.id} day={d} index={i} modulesById={modulesById} onChange={nd => setSchedule(schedule.map((x, j) => j === i ? nd : x))} onRemove={() => setSchedule(schedule.filter((_, j) => j !== i))} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
